import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { PortalDsAlert } from 'src/components/portal-design-system';
import { SchedulerDsButton } from './scheduler-ds-button';
import { ClientDataTransferForm } from './client-data-transfer-form';
import type { ClientDataTransfer, ClientDataTransferWritePayload } from '../types/data-transfers.types';

export type ClientDataTransferSheetMode = 'create' | 'edit';

interface ClientDataTransferSheetProps {
  open: boolean;
  mode: ClientDataTransferSheetMode;
  transfer: ClientDataTransfer | null;
  isSaving?: boolean;
  isDeleting?: boolean;
  submitError?: string | null;
  deleteError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: ClientDataTransferWritePayload) => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
}

export function ClientDataTransferSheet({
  open,
  mode,
  transfer,
  isSaving = false,
  isDeleting = false,
  submitError = null,
  deleteError = null,
  onOpenChange,
  onSubmit,
  onDelete,
}: ClientDataTransferSheetProps) {
  const isCreate = mode === 'create';
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const isBusy = isSaving || isDeleting;

  const handleConfirmDelete = async() => {
    if (!onDelete) {
      return;
    }
    try {
      await onDelete();
      setConfirmDeleteOpen(false);
    } catch {
      // deleteError is surfaced in the sheet.
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:!w-[70vw] sm:!max-w-none"
      >
        <div className="border-b border-gray-400 px-6 py-5">
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className="text-xl font-medium text-black">
              {isCreate ? 'New client data transfer' : transfer?.name ?? 'Client data transfer'}
            </SheetTitle>
            <SheetDescription className="text-sm text-gray-500">
              Configure name, description, SQL, and schedule for a client BigQuery export.
            </SheetDescription>
          </SheetHeader>
          {!isCreate && transfer ? (
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs font-medium text-gray-500">
                  Client
                </dt>
                <dd className="mt-0.5 text-black">
                  {transfer.client_title}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">
                  Next run
                </dt>
                <dd className="mt-0.5 text-black">
                  {transfer.next_run_at ? formatDisplayDateTime(transfer.next_run_at) : '—'}
                </dd>
              </div>
            </dl>
          ) : null}
        </div>

        <div className="flex-1 px-6 py-5">
          <ClientDataTransferForm
            initialTransfer={isCreate ? null : transfer}
            isSaving={isBusy}
            submitError={submitError}
            onSubmit={onSubmit}
            onCancel={() => onOpenChange(false)}
          />

          {!isCreate && onDelete ? (
            <div className="mt-8 border-t border-gray-200 pt-5">
              {deleteError ? (
                <PortalDsAlert
                  variant="danger"
                  className="mb-3"
                >
                  {deleteError}
                </PortalDsAlert>
              ) : null}
              <SchedulerDsButton
                type="button"
                variant="danger"
                disabled={isBusy}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  'Delete transfer'
                )}
              </SchedulerDsButton>
            </div>
          ) : null}
        </div>
      </SheetContent>

      <Dialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Delete client data transfer?
            </DialogTitle>
            <DialogDescription>
              This cannot be undone. Scheduled runs for this transfer will stop.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <SchedulerDsButton
              type="button"
              variant="secondary"
              onClick={() => setConfirmDeleteOpen(false)}
            >
              Cancel
            </SchedulerDsButton>
            <SchedulerDsButton
              type="button"
              variant="danger"
              disabled={isDeleting}
              onClick={handleConfirmDelete}
            >
              Delete
            </SchedulerDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}
